import { supabase } from '../context/AuthContext';

const MELBOURNE_TIMEZONE = 'Australia/Melbourne';

/** Paid orders only. Refunded labels such as "Refunded-16/10/2026" are excluded. */
export const canRefundOrderStatus = (status) =>
  String(status || '').trim().toLowerCase() === 'paid';

/** Original VIN plus a fixed "-REFUNDED" suffix. */
export const refundedVin = (vin) => {
  const original = String(vin || '').trim();
  if (!original) return '';
  if (/-REFUNDED$/i.test(original)) return original;
  return `${original}-REFUNDED`;
};

/** Fixed "Refunded-" prefix plus today's date in Melbourne, as DD/MM/YYYY. */
export const refundedStatusLabel = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: MELBOURNE_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(date);
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  return `Refunded-${day}/${month}/${year}`;
};

const restoreVin = (submissionId, vin, nextVin) =>
  supabase
    .from('modelreports')
    .update({ vin_number: vin })
    .eq('id', submissionId)
    .eq('vin_number', nextVin);

/** Updates VIN and payment status with the signed-in admin session. Does not call Stripe. */
export const refundOrder = async (submissionId, vin) => {
  const currentVin = String(vin || '').trim();
  const nextVin = refundedVin(currentVin);
  const nextStatus = refundedStatusLabel();

  if (!submissionId) {
    throw new Error('Order not found.');
  }
  if (!nextVin) {
    throw new Error('This order has no VIN to update.');
  }

  const { data: vinRows, error: vinError } = await supabase
    .from('modelreports')
    .update({ vin_number: nextVin })
    .eq('id', submissionId)
    .eq('is_archived', false)
    .eq('vin_number', currentVin)
    .select('id');

  if (vinError) {
    throw new Error(vinError.message || 'Failed to update VIN');
  }
  if (!vinRows?.length) {
    throw new Error('Could not update the VIN. Refresh and try again.');
  }

  const { data: statusRows, error: statusError } = await supabase
    .from('transactions')
    .update({ payment_status: nextStatus })
    .eq('submission_id', submissionId)
    .eq('payment_status', 'Paid')
    .select('id');

  if (statusError || !statusRows?.length) {
    await restoreVin(submissionId, currentVin, nextVin);
    throw new Error(statusError?.message || 'Could not update the status. The VIN was left unchanged.');
  }

  return {
    vin_number: nextVin,
    status: nextStatus,
  };
};
